import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';

/* Primary nav links — all 6 from HTML version */
const NAV_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/about-us', label: 'About Us' },
  { to: '/contact-us', label: 'Contact' },
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms-conditions', label: 'Terms & Conditions' },
  { to: '/cancellation-refund-policy', label: 'Refund Policy' },
];

/* Secondary links shown only in mobile drawer */
const DRAWER_EXTRA = [];

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Scrolled shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  return (
    <>
      <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="navbar-inner">

          {/* ── Brand ── */}
          <NavLink to="/" className="nav-brand" aria-label="Ab Meri Baari — Home">
            <div className="nav-logo-wrap">
              <img src="/512-app-icon.png" alt="" className="nav-logo" aria-hidden="true" />
            </div>
            <div className="nav-brand-text">
              <span className="nav-brand-name">Ab Meri Baari</span>
              <span className="nav-brand-sub">Kyunki Padhna Zaroori hai</span>
            </div>
          </NavLink>

          {/* ── Desktop links ── */}
          <ul className="nav-links" role="list">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* ── Desktop actions ── */}
          <div className="nav-actions">
            <a href="/#download-app" className="nav-pill" aria-label="Download the app">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17 12h-4V3H11v9H7l5 5 5-5zm-9 7v2h8v-2H8z" />
              </svg>
              Download App
            </a>
            <NavLink to="/contact-us" className="nav-cta">
              Get in Touch
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </NavLink>
          </div>

          {/* ── Hamburger ── */}
          <button
            className={`nav-toggle${open ? ' nav-toggle--open' : ''}`}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="nav-drawer"
            onClick={toggle}
          >
            <span className="bar bar-1" />
            <span className="bar bar-2" />
            <span className="bar bar-3" />
          </button>
        </div>
      </nav>

      {/* ── Mobile drawer ── */}
      <div
        id="nav-drawer"
        className={`nav-drawer${open ? ' nav-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="nav-drawer-inner">

          {/* Main links */}
          <div className="nav-drawer-section">
            <p className="nav-drawer-section-label">Menu</p>
            <ul className="nav-drawer-links" role="list">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) => `nav-drawer-link${isActive ? ' nav-drawer-link--active' : ''}`}
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal / secondary links */}
          {DRAWER_EXTRA.length > 0 && (
            <div className="nav-drawer-section">
              <p className="nav-drawer-section-label">Legal</p>
              <ul className="nav-drawer-links nav-drawer-links--sm" role="list">
                {DRAWER_EXTRA.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      className={({ isActive }) => `nav-drawer-link nav-drawer-link--sm${isActive ? ' nav-drawer-link--active' : ''}`}
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Download strip */}
          <div className="nav-drawer-app-strip">
            <div className="nav-drawer-app-text">
              <span className="nav-drawer-app-title">📱 Get the App</span>
              <span className="nav-drawer-app-sub">Learn on the go — offline ready</span>
            </div>
            <a href="/#download-app" className="nav-drawer-app-btn" onClick={() => setOpen(false)}>
              Download
            </a>
          </div>

          {/* CTA */}
          <NavLink to="/contact-us" className="nav-cta nav-cta--mobile">
            Get in Touch
          </NavLink>
        </div>
      </div>

      {/* Overlay */}
      {open && (
        <div
          className="nav-overlay"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}

export default Navbar;
